import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCommentSms, faXmark } from '@fortawesome/free-solid-svg-icons';
import { substituteSampleVars, smsCount } from './sampleVars';
import './SmsPreview.css';

interface SmsPreviewProps {
    /** Raw SMS text the business owner typed (may contain {variables}). */
    body: string;
    /** Business name used to fill the {business} sample variable. */
    businessName?: string | null;
}

/**
 * Client-side SMS preview.
 *
 * Unlike the e-mail preview there is no backend round-trip — an SMS is plain
 * text, so the sample variables are substituted right here and the result is
 * shown in a phone-style message bubble together with a live character /
 * segment counter (each 160-char GSM-7 segment is billed separately).
 *
 * CLOSED → "Build SMS" button.  OPEN → bubble preview + "Close".
 * The bubble reflects the textarea live because `body` is a prop.
 */
const SmsPreview: React.FC<SmsPreviewProps> = ({ body, businessName }) => {
    const { t, i18n } = useTranslation();
    const [open, setOpen] = useState(false);

    const rendered = substituteSampleVars(body, businessName, i18n.language);
    const count = smsCount(rendered);
    const segWord = count.segments === 1
        ? t('settings.smsPreview.segment')
        : t('settings.smsPreview.segments');

    return (
        <div className="sms-preview">
            <div className="sms-preview__actions">
                {!open ? (
                    <button
                        type="button"
                        className="sms-preview__btn"
                        onClick={() => setOpen(true)}
                        aria-expanded={false}
                    >
                        <FontAwesomeIcon icon={faCommentSms} className="sms-preview__btn-icon" />
                        {t('settings.smsPreview.open')}
                    </button>
                ) : (
                    <button
                        type="button"
                        className="sms-preview__close-btn"
                        onClick={() => setOpen(false)}
                        aria-expanded={true}
                    >
                        <FontAwesomeIcon icon={faXmark} className="sms-preview__btn-icon" />
                        {t('settings.smsPreview.close')}
                    </button>
                )}
            </div>

            {open && (
                <div className="sms-preview__panel">
                    <div className="sms-preview__phone">
                        <div className="sms-preview__sender">
                            {(businessName ?? '').trim() || t('settings.smsPreview.defaultSender')}
                        </div>
                        {rendered.trim() ? (
                            <div className="sms-preview__bubble">{rendered}</div>
                        ) : (
                            <div className="sms-preview__bubble sms-preview__bubble--empty">
                                {t('settings.smsPreview.emptyBubble')}
                            </div>
                        )}
                    </div>
                    <div className="sms-preview__meta">
                        <span>
                            {t('settings.smsPreview.chars', { count: count.chars })}
                        </span>
                        <span>·</span>
                        <span>
                            {count.segments} {segWord}
                        </span>
                        <span>·</span>
                        <span title={t('settings.smsPreview.hint', { perSegment: count.perSegment })}>
                            {count.encoding}
                        </span>
                    </div>
                    <p className="sms-preview__hint">
                        {t('settings.smsPreview.hint', { perSegment: count.perSegment })}
                    </p>
                </div>
            )}
        </div>
    );
};

export default SmsPreview;
